import React, { useContext } from 'react';
import styled from 'styled-components';
import { Redirect } from 'react-router-dom';
import { useAuthPrivilege } from 'hooks/useAuthPrivilege';
import { Context } from 'components/auth/context';
import Button from 'components/button';
import Colors from 'styles/colors';
import { environmentOptions } from 'utils/helpers';
import {ReactComponent as BeamLockSVG} from 'svg/beam-graphic.svg';
import { sendMessage } from "registerServiceWorker";
import { AUTH_TOKEN, LOCAL_STORAGE } from '../constants';
import useBeamLockAnim from './useBeamLockAnim';
import { getCookieByName } from 'utils/cookiesHelper';

export const CssPermissionsContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 20px;
  background-color: #F6F6F6;
  height: 100vh;
`;

export const CssSvgContainer = styled.div`
  margin: -40px auto 0;
  text-align: center;

  .auth-anime-message {
    opacity: 0;
    transition: all 300ms ease-in-out;

    &.-auth-anime-exit-done {
      opacity: 1;
    }

    &.-auth-anime-enter,
    &.-auth-anime-enter-done {
      opacity: 1;
    }
  }

  h1 {
    font-size: 32px;
    font-weight: 700;
    margin: 10px 0 0;
    color: ${Colors.oxfordBlue};
  }

  p {
    font-size: 18px;
    font-weight: 300;
    color: ${Colors.oxfordBlue};
    margin: 0 0 15px 0;
  }

  .go-back-button {
    margin-right: 10px;
  }

  #Oval { opacity: 0 }
  #ring { opacity: 0 }
`;

export const AuthPermissions = ({ privilege, history, children }) => {
  const { hasPrivilege, loading, error } = useAuthPrivilege({ privilege });
  const { dispatch } = useContext(Context);

  useBeamLockAnim();

  function logout() {
    dispatch({ type: "logout" });
    const newData = { credentials: {}};
    sendMessage(newData);
    history.push('/login');
  }

  // Do not redirect to the API error page on a 403 error.
  // We let a login modal handle this error.
  if (error && error?.networkError?.statusCode !== 403) {
    return (
      <Redirect to={{
        pathname: '/api-error',
      }} />
    );
  }

  if (hasPrivilege || !privilege) return children;

  return (
    <CssPermissionsContainer>
      <CssSvgContainer>
        <BeamLockSVG />
        {loading && <p>Checking permissions...</p>}
        {!loading && (<>
          <h1>Hold up!</h1>
          <p>You don't have the correct permissions to access this page.</p>
          <Button color="havelockBlue" icon="arrow-left" onClick={() => history.goBack()} className="go-back-button">Go Back</Button>
          <Button color="greyChateau" icon="sign-out" onClick={logout}>Sign Out</Button>
        </>)}
      </CssSvgContainer>
    </CssPermissionsContainer>
  );
};

/**
 * The localStorage key for the auth token of the current (or given)
 * environment and the current host. Computed at call time so writes always
 * land under the environment that is active when they happen.
 */
function tokenKey(env?: string) {
  const ENV_OPTIONS = environmentOptions();
  return `${AUTH_TOKEN}:${env || ENV_OPTIONS.X_ENV}:${ENV_OPTIONS.X_HOST_SHORTNAME}`;
}

/**
 * Read and validate a stored token entry. A malformed entry (unparseable, or
 * missing tokenCreate.token / expiresAt) is removed so it can't crash later
 * reads, and null is returned.
 */
function readTokenEntry(key: string) {
  const raw = localStorage.getItem(key);
  if (!raw) return null;

  try {
    const { token, expiresAt } = JSON.parse(raw)?.tokenCreate ?? {};
    if (!token || !expiresAt) throw new Error('malformed auth token entry');
    return { token, expiresAt, raw };
  } catch (_) {
    localStorage.removeItem(key);
    return null;
  }
}

/**
 * Remove every auth-token entry (any environment, any host) that is
 * unparseable or expired. Valid tokens for the sibling environment are kept —
 * the header's environment toggle relies on them to switch without re-login.
 */
function sweepStaleTokens() {
  Object.keys(localStorage)
    .filter((key) => key.startsWith(`${AUTH_TOKEN}:`))
    .forEach((key) => {
      const entry = readTokenEntry(key);
      if (entry && Date.parse(entry.expiresAt) < new Date().getTime()) {
        localStorage.removeItem(key);
      }
    });
}

/**
 * Clear the auth token from localStorage.
 */
function clearToken() {
  localStorage.removeItem(tokenKey());
}

/**
 * The single writer for the auth token. Sweeps stale entries first so a
 * login never accumulates dead auth-token keys.
 * @param data The login mutation result: { tokenCreate: { token, expiresAt } }
 */
function setToken(data) {
  sweepStaleTokens();
  localStorage.setItem(tokenKey(), JSON.stringify(data));
}

/**
 * Set cookie data (from a sibling site) to local storage. Rejects payloads
 * that don't match the login mutation shape so a bad cookie can't poison
 * localStorage.
 * @returns The stored token string, or false when the payload was rejected.
 */
function storeCookieToLocalStorage(cookieData) {
  const ENV_OPTIONS = environmentOptions();
  const host = ENV_OPTIONS.X_HOST_SHORTNAME;

  try {
    const { sites, token, env } = JSON.parse(cookieData);

    if (!sites?.includes(host) || !token) return false;
    if (env !== 'stage' && env !== 'production') return false;

    const { token: parsedToken, expiresAt } = JSON.parse(token)?.tokenCreate ?? {};
    if (!parsedToken || !expiresAt) return false;

    sweepStaleTokens();
    localStorage.setItem(tokenKey(env), token);
    localStorage.setItem(`${LOCAL_STORAGE.X_ENV}:${host}`, env);

    return token;
  } catch (_) {
    return false;
  }
}

/**
 * Check if the user has a token in local storage that has not expired.
 */
const Auth = {
  isUserAuthenticated() {
    const entry = readTokenEntry(tokenKey());
    if (!entry) return false;

    if (Date.parse(entry.expiresAt) < new Date().getTime()) {
      clearToken();
      return false;
    }

    return true;
  },

  /**
   * Useful for accessing the token, such as in a Viewer Query.
   */
  getToken: () => readTokenEntry(tokenKey())?.token ?? '',
  clearToken,
  setToken,
  sweepStaleTokens,
  storeCookieToLocalStorage,

  /**
   * Useful for accessing the full token for passing cookie data to sibling sites.
   */
  getFullToken: () => readTokenEntry(tokenKey())?.raw ?? '',
};

export default Auth;
